import React from 'react';
import { MyClientsOrderEvent } from '@1po/1po-bff-fe-spec/generated/my_clients_order/model/MyClientsOrderEvent';
import { MyClientsOrderEventType } from '@1po/1po-bff-fe-spec/generated/my_clients_order/model/MyClientsOrderEventType';
import { MyClientsOrderLine } from '@1po/1po-bff-fe-spec/generated/my_clients_order/model/MyClientsOrderLine';
import type { ColumnsType } from 'antd-v5/es/table';
import { TFunction } from 'i18next';
import { getMyClientsOrderStatus } from 'pages/MyOrdersPage/ClientsOrders/ClientsOrders.utils';
import { CenterFlex, Flex } from 'UI';
import { textFormatter } from 'utils';
import { isValidDate } from 'utils/format/formattingService';

const getEventTypeLabel = (eventType: MyClientsOrderEventType, t: TFunction): string => {
  switch (eventType) {
    case 'ACKNOWLEDGED_FROM_DMS':
      return t('clients_order.event_type.acknowledged_from_dms', 'Acknowledged from DMS');
    case 'MAIL_SENT_TO_R1':
      return t('clients_order.event_type.mail_sent_to_r1', 'Mail sent to R1');
    case 'ORDER_CREATED':
      return t('clients_order.event_type.order_created', 'Order created');
    case 'ORDER_STATUS_CHANGED':
      return t('clients_order.event_type.order_status_changed', 'Order status changed');
    case 'ORDER_REJECTED':
      return t('clients_order.event_type.order_rejected', 'Order rejected');
    case 'SENT_TO_DMS':
      return t('clients_order.event_type.sent_to_dms', 'Sent to DMS');
    case 'VALIDATED':
      return t('clients_order.event_type.validated', 'Validated');
    case 'UNKNOWN':
    default:
      return t('clients_order.event_type.unknown', 'Unknown');
  }
};

/**
 * Defines table columns for order lines (references)
 */
export const getLinesColumns = (t: TFunction, currency?: string): ColumnsType<MyClientsOrderLine> => [
  {
    title: <span data-cy={'col-header-reference'}>{t('clients_order.detail.reference', 'References')}</span>,
    dataIndex: 'reference',
    key: 'reference',
    render: (v?: string) => <CenterFlex data-cy={'col-result-reference'}>{v ?? ''}</CenterFlex>,
  },
  {
    title: <span data-cy={'col-header-designation'}>{t('clients_order.detail.designation', 'Designation')}</span>,
    dataIndex: 'designation',
    key: 'designation',
    render: (v?: string) => <Flex data-cy={'col-result-designation'}>{v ?? ''}</Flex>,
  },
  {
    title: <span data-cy={'col-header-delivered-by'}>{t('clients_order.detail.delivered_by', 'Delivered by')}</span>,
    dataIndex: 'supplierName',
    key: 'supplierName',
    render: (v?: string) => <CenterFlex data-cy={'col-result-delivered-by'}>{v ?? ''}</CenterFlex>,
  },
  {
    title: <span data-cy={'col-header-ordered-qty'}>{t('clients_order.detail.ordered_qty', 'Ordered Qty')}</span>,
    dataIndex: 'orderedQuantity',
    key: 'orderedQuantity',
    render: (v?: number) => <CenterFlex data-cy={'col-result-ordered-qty'}>{v ?? ''}</CenterFlex>,
  },
  {
    title: <span data-cy={'col-header-urgent'}>{t('clients_order.detail.urgent', 'Urgent')}</span>,
    dataIndex: 'urgent',
    key: 'urgent',
    render: (v = false) => (
      <CenterFlex data-cy={'col-result-urgent'}>{v ? t('common.yes', 'Yes') : t('common.no', 'No')}</CenterFlex>
    ),
  },
  {
    title: (
      <span data-cy={'col-header-unit-price'}>{t('clients_order.detail.unit_price', 'Unit price (VAT Excl.)')}</span>
    ),
    dataIndex: 'unitPriceTaxFree',
    key: 'unitPriceTaxFree',
    render: (v?: string) => (
      <CenterFlex data-cy={'col-result-unit-price'}>
        {textFormatter.formatCurrency(Number(v ?? 0), currency)}
      </CenterFlex>
    ),
  },
  {
    title: <span data-cy={'col-header-discount'}>{t('clients_order.detail.discount', 'Discount')}</span>,
    dataIndex: 'discountRate',
    key: 'discountRate',
    render: (v?: string) => <CenterFlex data-cy={'col-result-discount'}>{v ? `${v}%` : ''}</CenterFlex>,
  },
  {
    title: <span data-cy={'col-header-vat'}>{t('clients_order.detail.vat', 'VAT')}</span>,
    dataIndex: 'taxRate',
    key: 'taxRate',
    render: (v?: string) => <CenterFlex data-cy={'col-result-vat'}>{v ? `${v}%` : ''}</CenterFlex>,
  },
  {
    title: <span data-cy={'col-header-amount'}>{t('clients_order.detail.amount', 'Amount')}</span>,
    dataIndex: 'finalAmount',
    key: 'finalAmount',
    render: (v?: string) => (
      <CenterFlex data-cy={'col-result-amount'}>{textFormatter.formatCurrency(Number(v ?? 0), currency)}</CenterFlex>
    ),
  },
  {
    title: <span data-cy={'col-header-status'}>{t('order.order_detail.status', 'Status')}</span>,
    dataIndex: 'status',
    key: 'status',
    render: (v?: string) => (
      <CenterFlex data-cy={'col-result-status'}>{v ? getMyClientsOrderStatus(v, t) : ''}</CenterFlex>
    ),
  },
];

/**
 * Defines table columns for order events
 */
export const getEventsColumns = (t: TFunction): ColumnsType<MyClientsOrderEvent> => [
  {
    title: <span data-cy={'col-header-event-type'}>{t('clients_order.detail.event_type', 'Event type')}</span>,
    dataIndex: 'eventType',
    key: 'eventType',
    render: (v?: MyClientsOrderEventType) => (
      <CenterFlex data-cy={'col-result-event-type'}>{v ? getEventTypeLabel(v, t) : ''}</CenterFlex>
    ),
  },
  {
    title: <span data-cy={'col-header-date'}>{t('common.date', 'Date')}</span>,
    dataIndex: 'date',
    key: 'date',
    render: (v?: string) => {
      if (!v || !isValidDate(v)) return <CenterFlex data-cy={'col-result-date'}>—</CenterFlex>;
      return <CenterFlex data-cy={'col-result-date'}>{textFormatter.formatTime(new Date(v))}</CenterFlex>;
    },
  },
  {
    title: <span data-cy={'col-header-comment'}>{t('clients_order.detail.comment', 'Comment')}</span>,
    dataIndex: 'eventDescription',
    key: 'eventDescription',
    render: (v?: string) => <CenterFlex data-cy={'col-result-comment'}>{v ?? ''}</CenterFlex>,
  },
];
